import * as React from 'react';
import { sourceSystemApi } from '../services/sourceSystemApi';
import type { CommitHistoryItem } from '../types/source-system';

export interface UseCommitHistoryState {
  items: CommitHistoryItem[];
  total: number;
  page: number;
  pageSize: number;
  loading: boolean;
  error: string | null;
}

/**
 * Fetches a single page of commit/PR history for a project.
 * Re-fetches when projectUuid, page, or pageSize change.
 */
export function useCommitHistory(
  projectUuid: string,
  page: number,
  pageSize: number
): UseCommitHistoryState & { reload: () => void } {
  const [items, setItems] = React.useState<CommitHistoryItem[]>([]);
  const [total, setTotal] = React.useState(0);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    setItems([]);
    setError(null);
    try {
      const data = await sourceSystemApi.getCommitHistory(projectUuid, page, pageSize);
      setItems(data.items);
      setTotal(data.total);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [projectUuid, page, pageSize]);

  React.useEffect(() => {
    void load();
  }, [load]);

  return { items, total, page, pageSize, loading, error, reload: load };
}
